<template>
  <div id="main-wrapper">
    <AdminNavbar />

    <div class="page-title">
      <div class="container">
        <div class="row">
          <div class="col-lg-12 col-md-12">
            <h2 class="ipt-title">{{ t('Welcome!') }}</h2>
          </div>
        </div>
      </div>
    </div>

    <section class="bg-light">
      <div class="container-fluid">
        <div class="row">
          <div class="col-lg-3 col-md-12">
            <AdminSidebar :open="show" @update:open="show = $event" :user="user" />
          </div>

          <div class="col-lg-9 col-md-12">
            <div class="dashboard-wraper">
              <h6 class="mb-3">
                {{ t('Soon Exhibits') }} - {{ t('Select your preferences so we can email you when it begins.') }}
              </h6>

              <div class="row g-3">
                <div
                  v-for="exhibit in exhibits"
                  :key="exhibit.id"
                  class="col-lg-3 col-md-4 col-sm-6"
                >
                  <div
                    class="exhibit-card"
                    :class="{ selected: selectedExhibits.some(e => e.id === exhibit.id) }"
                    @click="openModal(exhibit)"
                  >
                    <img :src="exhibit.logo" alt="exhibit logo" class="exhibit-logo mb-2" />
                    <h6 style="color:#6A715D">{{ exhibit.name }}</h6>
                  </div>
                </div>
              </div>

              <!-- Modal -->
              <div v-if="modalExhibit" class="modal-overlay" @click.self="closeModal">
                <div class="modal-content">
                  
                  <div class="modal-header">
                    <h5>{{ modalExhibit.name }}</h5>
                    <button class="close-btn" @click="closeModal">&times;</button>
                  </div>
                  
                  <div class="modal-body">
                    <p v-html="modalExhibit?.description || ''" class="description"></p>

                    <p>
                      <strong>{{ $t('Start Date') }}:</strong> {{ modalExhibit?.start_date }}<br>
                      <strong>{{ $t('End Date') }}:</strong> {{ modalExhibit?.end_date }}
                    </p>

                    <p><strong>{{ $t('Number of Halls') }}:</strong> {{ modalExhibit?.halls_count }}</p>
                    <ul v-if="modalExhibit?.halls?.length" class="hall-list">
                      <li v-for="hall in modalExhibit.halls" :key="hall.id">
                        <strong>{{ hall.name }}:</strong> {{ $t('Number of Stands') }}: {{ hall.stands_count }}
                      </li>
                    </ul>

                    <p>
                      <i class="fa-solid fa-location-dot px-2"></i>
                      {{ modalExhibit.location }}
                    </p>

                    <div class="notification-settings mt-3">
                      <label>
                        {{ t('Select Day for Notification') }}:
                        <input type="date" v-model="notificationDay" class="form-control" />
                      </label>

                      <label class="mt-2">
                        {{ t('Number of Notifications') }}:
                        <input type="number" min="1" v-model.number="notificationTimes" class="form-control" />
                      </label>
                    </div>
                  </div>
                  
                  <div class="modal-actions">
                    <button class="btn btn-secondary" @click="closeModal">{{ t('Close') }}</button>
                    
                    <button
                      v-if="!isSelected(modalExhibit.id)"
                      class="btn btn-primary"
                      @click="addInterested(modalExhibit)"
                    >
                      {{ t('Interested') }}
                    </button>

                    <button
                      v-else
                      class="btn btn-warning"
                      @click="updateInterested(modalExhibit)"
                    >
                      {{ t('Update') }}
                    </button>

                    <button
                      v-if="isSelected(modalExhibit.id)"
                      class="btn btn-danger"
                      @click="removeInterested(modalExhibit)"
                    >
                      {{ t('Uninterested') }}
                    </button>
                  </div>
                </div>
              </div>

              <button class="btn btn-primary mt-4" @click="submitExhibits">
                {{ t('Submit') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FooterTop :bg="'theme-bg'" />
    <FooterDark />
    <ScrollToTop />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'

import AdminSidebar from '@/components/admin-sidebar.vue'
import AdminNavbar from '@/components/navbar/admin-navbar.vue'
import FooterTop from '@/components/footer/footer-top.vue'
import FooterDark from '@/components/footer/footer-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'

const { t, locale } = useI18n()
const toast = useToast()
const router = useRouter()

const show = ref(false)
const user = ref(null)
const exhibits = ref([])
const selectedExhibits = ref([])
const modalExhibit = ref(null)

const notificationDay = ref('')
const notificationTimes = ref(1)

const token = localStorage.getItem('auth_token')

onMounted(async () => {
  if (!token) {
    toast.error(t('You are not logged in.'))
    router.replace('/404')
    return
  }

  try {
    const userResponse = await axios.get('/visitor', {
      headers: { Authorization: `Bearer ${token}` }
    })
    user.value = userResponse.data.user

    const exhibitsResponse = await axios.get('/exhibits-for-visitor', {
      params: { lang: locale.value },
      headers: { Authorization: `Bearer ${token}` }
    })
    exhibits.value = exhibitsResponse.data

    selectedExhibits.value = exhibits.value
      .filter(e => e.interested) 
      .map(e => ({
        id: e.id,
        notify_day: e.notify_day || '',     
        notify_times: e.notify_times || 1  
      }))
  } catch (error) {
    toast.error(t('Failed to load data'))
    router.replace('/404')
  }
})

const isSelected = (exhibitId) => selectedExhibits.value.some(e => e.id === exhibitId)

const openModal = (exhibit) => {
  modalExhibit.value = exhibit

  const selected = selectedExhibits.value.find(e => e.id === exhibit.id)
  if (selected) {
    notificationDay.value = selected.notify_day   
    notificationTimes.value = selected.notify_times  
  } else {
    notificationDay.value = ''
    notificationTimes.value = 1
  }
}


const closeModal = () => { modalExhibit.value = null }

const addInterested = (exhibit) => {
  if (!notificationDay.value) {
    toast.error(t('Please select a day for notification'))
    return
  }
  selectedExhibits.value.push({
    id: exhibit.id,
    notify_day: notificationDay.value,
    notify_times: notificationTimes.value
  })
  modalExhibit.value = null
}

const updateInterested = (exhibit) => {
  const index = selectedExhibits.value.findIndex(e => e.id === exhibit.id)
  if (index !== -1) {
    if (!notificationDay.value) {
      toast.error(t('Please select a day for notification'))
      return
    }
    selectedExhibits.value[index].notify_day = notificationDay.value
    selectedExhibits.value[index].notify_times = notificationTimes.value
  }
  modalExhibit.value = null
}

const removeInterested = (exhibit) => {
  const index = selectedExhibits.value.findIndex(e => e.id === exhibit.id)
  if (index !== -1) {
    selectedExhibits.value.splice(index, 1)
  }
  modalExhibit.value = null
}

const submitExhibits = async () => {
  try {
    await axios.post(
      '/visitor-exhibits',
      { exhibits: selectedExhibits.value },
      {
        params: { lang: locale.value },
        headers: { Authorization: `Bearer ${token}` },
      }
    )
    toast.success(t('Exhibits saved successfully'))
  } catch (error) {
    if (error.response && error.response.status === 422) {
      const errors = error.response.data.errors
      for (const key in errors) {
        toast.error(errors[key][0])
      }
    } else if (error.response && error.response.data.message) {
      toast.error(error.response.data.message)
    } else {
      toast.error(t('Failed to save exhibits'))
    }
    console.error('Submit exhibits error:', error)
  }
}
</script>

<style scoped>
.exhibit-card {
  border: 1px solid #ddd;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fff;
}
.exhibit-card.selected {
  background-color: #C48B6E;
  color: #fff;
}
.exhibit-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}
.exhibit-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s;
}
.modal-content {
  background-color: #fff;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  animation: slideIn 0.3s;
  overflow-y: auto;
  max-height: 90vh;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}
.modal-body {
  line-height: 1.5;
  color: #333;
}
.hall-list {
  padding-left: 1rem;
  margin: 0.5rem 0;
  list-style-type: disc;
}
.notification-settings label {
  display: block;
  margin-bottom: 0.5rem;
}
.notification-settings input {
  width: 100%;
  padding: 0.3rem 0.5rem;
  margin-top: 0.2rem;
  border-radius: 6px;
  border: 1px solid #ccc;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}
@keyframes fadeIn { from {opacity:0;} to {opacity:1;} }
@keyframes slideIn { from {transform: translateY(-20px);} to {transform: translateY(0);} }
</style>
